/**
 * Data layer for `igaming-s2s-pipeline`. Thin typed fetch wrappers over /api/v1/igaming-s2s-pipeline.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface IgamingS2sPipelineRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListIgamingS2sPipelineResponse {
	count: number;
	items: IgamingS2sPipelineRow[];
}

export interface CreateIgamingS2sPipelineBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/igaming-s2s-pipeline";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListIgamingS2sPipelineResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListIgamingS2sPipelineResponse>(res);
}

export async function create(body: CreateIgamingS2sPipelineBody): Promise<IgamingS2sPipelineRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<IgamingS2sPipelineRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const igamingS2sPipelineQueryKeys = {
	all: ["igaming-s2s-pipeline"] as const,
	list: () => [...igamingS2sPipelineQueryKeys.all] as const,
	detail: (id: string) => [...igamingS2sPipelineQueryKeys.all, id] as const,
};
